import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import {
  CurrencySelect,
  CURRENCY_NO_CONVERSION_NOTE,
} from '@/app/components/currency-select';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { Switch } from '@/app/components/ui/switch';
import { useCreateInsurancePolicy } from '@/hooks/use-comply';
import { useOrgCurrency } from '@/hooks/use-settings';
import { handleApiError } from '@/lib/api-client';
import type { CreateInsurancePolicyPayload, InsurancePolicyType } from '@/lib/types';

export function InsuranceNewPage() {
  const navigate = useNavigate();
  const createMutation = useCreateInsurancePolicy();
  const orgCurrency = useOrgCurrency();

  const [form, setForm] = useState<CreateInsurancePolicyPayload>({
    policy_type: 'public_liability',
    policy_name: '',
    insurer: '',
    policy_number: '',
    coverage_amount: '',
    currency: '',
    start_date: '',
    expiry_date: '',
    auto_renew: false,
    covers_rope_access: true,
    broker_name: '',
    broker_contact: '',
    notes: '',
  });

  const [error, setError] = useState<string | null>(null);

  const updateField = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (
      !form.policy_name ||
      !form.insurer ||
      !form.policy_number ||
      !form.start_date ||
      !form.expiry_date
    ) {
      setError(
        'Policy name, insurer, policy number, start date, and expiry date are required.'
      );
      return;
    }

    try {
      const payload = {
        ...form,
        coverage_amount: form.coverage_amount || null,
      };
      await createMutation.mutateAsync(payload);
      navigate('/compliance/insurance');
    } catch (err) {
      const apiError = handleApiError(err);
      setError(apiError.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to="/compliance/insurance">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Add Policy</h1>
          <p className="text-gray-600 mt-1">Register a new insurance policy</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Policy Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="policy_type">Policy Type *</Label>
                <Select
                  value={form.policy_type}
                  onValueChange={(v) =>
                    updateField('policy_type', v as InsurancePolicyType)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public_liability">Public Liability</SelectItem>
                    <SelectItem value="employers_liability">
                      Employers&apos; Liability
                    </SelectItem>
                    <SelectItem value="professional_indemnity">
                      Professional Indemnity
                    </SelectItem>
                    <SelectItem value="product_liability">Product Liability</SelectItem>
                    <SelectItem value="contractor_all_risk">
                      Contractor All Risk
                    </SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="policy_name">Policy Name *</Label>
                <Input
                  id="policy_name"
                  value={form.policy_name}
                  onChange={(e) => updateField('policy_name', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="insurer">Insurer *</Label>
                <Input
                  id="insurer"
                  value={form.insurer}
                  onChange={(e) => updateField('insurer', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="policy_number">Policy Number *</Label>
                <Input
                  id="policy_number"
                  value={form.policy_number}
                  onChange={(e) => updateField('policy_number', e.target.value)}
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Coverage &amp; Dates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="coverage_amount">Coverage Amount</Label>
                <Input
                  id="coverage_amount"
                  type="number"
                  step="0.01"
                  value={form.coverage_amount ?? ''}
                  onChange={(e) => updateField('coverage_amount', e.target.value)}
                  placeholder="e.g. 5000000"
                />
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <CurrencySelect
                  id="currency"
                  value={form.currency || orgCurrency}
                  onValueChange={(value) => updateField('currency', value)}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {CURRENCY_NO_CONVERSION_NOTE}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_date">Start Date *</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => updateField('start_date', e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="expiry_date">Expiry Date *</Label>
                <Input
                  id="expiry_date"
                  type="date"
                  value={form.expiry_date}
                  onChange={(e) => updateField('expiry_date', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  id="auto_renew"
                  checked={form.auto_renew}
                  onCheckedChange={(v) => updateField('auto_renew', v)}
                />
                <Label htmlFor="auto_renew">Auto-renew</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="covers_rope_access"
                  checked={form.covers_rope_access}
                  onCheckedChange={(v) => updateField('covers_rope_access', v)}
                />
                <Label htmlFor="covers_rope_access">Covers rope access work</Label>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Broker Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="broker_name">Broker Name</Label>
                <Input
                  id="broker_name"
                  value={form.broker_name}
                  onChange={(e) => updateField('broker_name', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="broker_contact">Broker Contact</Label>
                <Input
                  id="broker_contact"
                  value={form.broker_contact}
                  onChange={(e) => updateField('broker_contact', e.target.value)}
                  placeholder="Email or phone"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={form.notes}
                onChange={(e) => updateField('notes', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-3">
          <Button type="submit" disabled={createMutation.isPending}>
            {createMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            Add Policy
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/compliance/insurance">Cancel</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}
